import { useEffect, useCallback } from 'react';
import { X, Download, ChevronLeft, ChevronRight } from 'lucide-react';

export interface LightboxImage {
  url: string;
  filename: string;
}

interface ImageLightboxProps {
  images: LightboxImage[];
  currentIndex: number;
  isOpen: boolean;
  onClose: () => void;
  onNavigate?: (index: number) => void;
}

// Stable default so goPrev/goNext (and the keyboard effect that depends on them) don't get a new
// identity every render when no onNavigate is supplied.
const noop = () => {};

export default function ImageLightbox({ images, currentIndex, isOpen, onClose, onNavigate = noop }: ImageLightboxProps) {
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < images.length - 1;
  const current = images[currentIndex];

  const goPrev = useCallback(() => {
    if (hasPrev) onNavigate(currentIndex - 1);
  }, [hasPrev, currentIndex, onNavigate]);

  const goNext = useCallback(() => {
    if (hasNext) onNavigate(currentIndex + 1);
  }, [hasNext, currentIndex, onNavigate]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') { e.preventDefault(); goPrev(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); goNext(); }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = 'unset';
      };
    }
  }, [isOpen, onClose, goPrev, goNext]);

  if (!isOpen || !current) {
    return null;
  }

  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = current.url;
    a.download = current.filename;
    a.target = '_blank';
    a.click();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />

      {/* Prev Arrow */}
      {hasPrev && (
        <button
          type="button"
          onClick={goPrev}
          className="absolute left-4 z-20 p-2 rounded-full bg-background/90 hover:bg-background border border-border transition-colors"
          title="Previous image"
          aria-label="Previous image"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
      )}

      {/* Next Arrow */}
      {hasNext && (
        <button
          type="button"
          onClick={goNext}
          className="absolute right-4 z-20 p-2 rounded-full bg-background/90 hover:bg-background border border-border transition-colors"
          title="Next image"
          aria-label="Next image"
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      )}

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center gap-4 max-w-[90vw] max-h-[90vh]">
        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute -top-12 right-0 p-2 rounded-md bg-background/90 hover:bg-background border border-border transition-colors"
          title="Close (ESC)"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Image */}
        <img
          src={current.url}
          alt={current.filename}
          className="max-w-full max-h-[calc(90vh-80px)] object-contain rounded-lg border border-border"
        />

        {/* Info Bar */}
        <div className="flex items-center gap-4 px-4 py-3 rounded-md bg-background/90 border border-border">
          {images.length > 1 && (
            <span className="text-xs text-muted-foreground">{currentIndex + 1}/{images.length}</span>
          )}
          <p className="text-sm font-medium text-foreground">{current.filename}</p>
          <button
            type="button"
            onClick={handleDownload}
            className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30 transition-colors"
          >
            <Download className="h-3.5 w-3.5" />
            Download
          </button>
        </div>
      </div>
    </div>
  );
}
